/**
 * Drivers one-shot opener bodies (Lead Ads → Baileys, fired ~60s after capture).
 *
 * All variants are SERVICE-scope: a transactional reply to the driver's own Lead
 * Ads submission. They MUST stay free of marketing lexicon (earnings, rates,
 * "get started", …) or the sender-gateway's category-at-send guard blocks the
 * send (see `findMarketingLexicon`). Each is a pure function of the captured lead
 * so it is trivially unit-testable and safe to A/B behind
 * `firstMessage.experiment`.
 *
 * Baseline (control, live since the 2026-06-28 Baileys cutover): personalizes
 * name + city and asks the single highest-value qualifier (PrDP — the #1 SA
 * dropout gate) with concrete reply options. Measured reply rate ~76% over
 * 2026-06-28→07-05 (302 contacts). See
 * docs/wip/active/WIP_fixing_drivers_opener_yield_2026_07_05.md.
 *
 * Spintax (anti-ban): the greeting, the opt-in anchor sentence, and the bridge
 * to the question are each drawn from a small bank of SEMANTICALLY EQUIVALENT
 * phrasings, seeded deterministically off the lead so the same lead always gets
 * the same body (testable, and consistent per conversation) while bodies differ
 * ACROSS recipients. Identical bodies sent to many recipients are the #1 content
 * ban signal for unofficial WhatsApp senders (server-side similarity hashing
 * trips at ~20-30 identical sends); varied bodies defeat it. The functional core
 * (the qualifier question + concrete reply options) is held verbatim so reply
 * rate and downstream qualification are unaffected — this is lexical variation,
 * not a copy change. Every phrasing stays SERVICE-scoped and marketing-lexicon
 * free. See the `baileys-antiban` skill (content signals) for the rationale.
 */

import type { FirstMessageLead } from '@selenos/leads';

/**
 * Resolve the personalization slots shared by every opener variant: the driver's
 * first name (falls back to "there") and an ` in {city}` clause that is dropped
 * for empty or placeholder dropdown values ("Other"/"None"/… would otherwise
 * render "…to drive in Other").
 */
export function resolveNameCity(lead: FirstMessageLead): {
  name: string;
  where: string;
} {
  const name =
    typeof lead.firstName === 'string' && lead.firstName.trim().length > 0
      ? lead.firstName.trim()
      : 'there';

  const CITY_PLACEHOLDERS = new Set(['other', 'none', 'n/a', 'unknown']);
  const rawCity = lead.customFields.city;
  const city =
    typeof rawCity === 'string' &&
    rawCity.trim().length > 0 &&
    !CITY_PLACEHOLDERS.has(rawCity.trim().toLowerCase())
      ? rawCity.trim()
      : null;

  return { name, where: city ? ` in ${city}` : '' };
}

/**
 * Deterministic per-lead seed (FNV-1a over stable identifiers). Same lead →
 * same seed → same spun body; different leads → (almost always) different body.
 */
function openerSeed(lead: FirstMessageLead): number {
  const s = `${lead.phone ?? ''}|${lead.email ?? ''}|${lead.firstName ?? ''}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic choice from `arr`, decorrelated per call site via `salt`. */
function pick<T>(arr: readonly T[], seed: number, salt: number): T {
  const idx = ((seed ^ Math.imul(salt, 2654435761)) >>> 0) % arr.length;
  return arr[idx] as T;
}

const GREETINGS = ['Hi', 'Hey', 'Hello'] as const;

/**
 * The human this number speaks as. Every outbound body, the WhatsApp Business
 * profile, and the AI agent persona MUST agree on this: a lead who gets an
 * email from Rovvo, a WhatsApp from "the Uber Hero team", and sees a profile
 * called "Drivers South Africa" is exactly the "surprise" that WhatsApp's
 * Commerce Policy s.1 forbids.
 */
export const SENDER_PERSON = 'Naledi';
export const SENDER_BRAND = 'Rovvo';

/**
 * Opt-in anchor sentence: greets by name, says who we actually are, and
 * references the driver's own application (so the recipient never reads it as
 * "didn't sign up"). Each phrasing ends on `to drive${where}` so the
 * placeholder-city drop still works.
 *
 * COMPLIANCE (do not regress): we are Rovvo, an independent service that helps
 * South African drivers get set up with Uber and Bolt. We are NOT Uber. The
 * previous copy opened "this is the Uber Hero team", which reads as an official
 * Uber channel and is squarely what Commerce Policy s.1 means by "Do not
 * impersonate another business or otherwise mislead customers as to the nature
 * of your business" / "misrepresent your affiliation with a business or
 * entity". That string was in the FIRST message to every lead while the number
 * was restricted for a s.1 "Quality Experience" violation on 2026-07-22/23/24.
 * Name the platform only as the thing we help them join, never as us.
 * See docs/guides/operations/whatsapp-business-identity.md
 */
const ANCHORS: ReadonlyArray<(g: string, n: string, w: string) => string> = [
  (g, n, w) =>
    `${g} ${n}, it's ${SENDER_PERSON} from ${SENDER_BRAND}, thanks for your application to drive${w}.`,
  (g, n, w) =>
    `${g} ${n}, ${SENDER_PERSON} here from ${SENDER_BRAND}. Thanks for applying to drive${w}.`,
  (g, n, w) =>
    `${g} ${n}, it's ${SENDER_PERSON} from ${SENDER_BRAND}. Thanks for putting in your application to drive${w}.`,
];

/** Bridge into the single qualifier question. */
const BRIDGES = [
  'To help get you set up, quick question:',
  'To get you set up, one quick question:',
  'So we can set you up right, quick question:',
] as const;

/** Compose the spun preamble (anchor + bridge) shared by every variant. */
function spunPreamble(lead: FirstMessageLead): string {
  const { name, where } = resolveNameCity(lead);
  const seed = openerSeed(lead);
  const greet = pick(GREETINGS, seed, 1);
  const anchor = pick(ANCHORS, seed, 2)(greet, name, where);
  const bridge = pick(BRIDGES, seed, 3);
  return `${anchor} ${bridge}`;
}

/**
 * CONTROL — the live opener. Leads with the PrDP qualifier (highest-value gate)
 * and concrete reply options. Preamble is spun per lead; the question is verbatim.
 */
export function driversOpenerControl(lead: FirstMessageLead): string {
  return (
    `${spunPreamble(lead)} ` +
    `do you already have your PrDP (Professional Driving Permit)? ` +
    `Reply Yes, Not yet, or "what's that?"`
  );
}

/**
 * CHALLENGER — intent/timing. Same personalization + spun preamble, but the
 * single question is a low-friction forward-looking one ("when are you looking
 * to start?") instead of a permit-qualification hurdle. Hypothesis: lower
 * cognitive load lifts reply rate above the control (guardrail: downstream
 * qualified/delivered must not drop, since the PrDP question front-loads
 * qualification). Concrete options keep the reply one-tap.
 */
export function driversOpenerIntent(lead: FirstMessageLead): string {
  return (
    `${spunPreamble(lead)} ` +
    `when are you looking to start? ` +
    `Reply "This week", "This month", or "Just exploring".`
  );
}
